/**
 * Risk of Bias / Quality Appraisal page
 *
 * Accessed from the Extraction page for INCLUDED/EXTRACTED studies.
 * Supports QUADAS-2 (Assessment), RoB 2.0 (Treatment), PEDro, and Custom tools.
 *
 * What is Risk of Bias?
 * Every systematic review must assess the methodological quality of each included
 * study. "Risk of bias" means: could the study design, conduct, or reporting have
 * produced a result that is systematically different from the truth? High risk of
 * bias doesn't disqualify a study — it qualifies the conclusions we can draw from it.
 *
 * QUADAS-2: used for diagnostic accuracy studies (Assessment review). Assesses
 *   whether a new test (here: XR assessment tool) is accurate compared to a gold standard.
 * RoB 2.0:  used for randomised trials (Treatment review). The gold-standard tool
 *   for evaluating whether randomisation was truly unbiased.
 * PEDro:    a simpler 11-item checklist for clinical trials in rehabilitation.
 * COSMIN:   for studies on the measurement properties of an instrument (validity,
 *   reliability, responsiveness of an XR assessment). Ten boxes, each rated very
 *   good / adequate / doubtful / inadequate or not applicable; worst score counts.
 * Custom:   for Technical/feasibility studies without an established tool.
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { formatUsageWithCost, useCostDisplay } from "../lib/cost.ts";
import { CostEstimate } from "../components/CostEstimate.tsx";
import { useUnsavedChanges } from "../lib/useUnsavedChanges.tsx";
import { SaveControls } from "../components/SaveControls.tsx";
import {
  Loader2, Wand2, ShieldCheck, AlertCircle, ArrowLeft, Info,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Domain {
  key: string;
  label: string;
  guidance: string;
}

interface ToolDef {
  label: string;
  description: string;
  domains: Domain[];
  ratings: readonly string[];
  overallRatings: readonly string[];
  overallRule?: "WORST_SCORE"; // COSMIN: the lowest rating among applicable items is the overall rating
}

interface Assessment {
  id: string;
  studyId: string;
  reviewId: string;
  tool: string;
  ratings: Record<string, string>;
  rationale: Record<string, string>;
  overallRating: string | null;
  isAiSuggested: boolean;
  isConfirmed: boolean;
  assessedBy: string | null;
  assessor?: { name: string } | null;
  updatedAt: string;
}

interface RobData {
  assessment: Assessment | null;
  suggestedTool: string;
  tools: Record<string, ToolDef>;
}

// ─── Rating colour helpers ─────────────────────────────────────────────────────

const RATING_COLOURS: Record<string, string> = {
  LOW: "bg-green-100 text-green-800 border-green-200",
  HIGH: "bg-red-100 text-red-800 border-red-200",
  UNCLEAR: "bg-amber-100 text-amber-800 border-amber-200",
  SOME_CONCERNS: "bg-yellow-100 text-yellow-800 border-yellow-200",
  NO_INFORMATION: "bg-gray-100 text-gray-700 border-gray-200",
  MODERATE: "bg-amber-100 text-amber-800 border-amber-200",
  YES: "bg-green-100 text-green-800 border-green-200",
  NO: "bg-red-100 text-red-800 border-red-200",
  // COSMIN
  VERY_GOOD: "bg-green-100 text-green-800 border-green-200",
  ADEQUATE: "bg-lime-100 text-lime-800 border-lime-200",
  DOUBTFUL: "bg-amber-100 text-amber-800 border-amber-200",
  INADEQUATE: "bg-red-100 text-red-800 border-red-200",
  NOT_APPLICABLE: "bg-gray-100 text-gray-700 border-gray-200",
};

/** "NOT_APPLICABLE" -> "NOT APPLICABLE" (the old replace("_", " ") only swapped the first underscore). */
const ratingLabel = (r: string) => r.replaceAll("_", " ");

/** COSMIN "worst score counts": the lowest-ranked rating among applicable items, or null. */
function worstScore(tool: ToolDef, ratings: Record<string, string>): string | null {
  const ranks = tool.domains
    .map((d) => tool.overallRatings.indexOf(ratings[d.key] ?? ""))
    .filter((i) => i >= 0);
  return ranks.length ? tool.overallRatings[Math.max(...ranks)] ?? null : null;
}

function RatingBadge({ rating }: { rating?: string }) {
  if (!rating) return <span className="text-xs text-muted-foreground">-</span>;
  const cls = RATING_COLOURS[rating] ?? "bg-secondary text-foreground border-border";
  return (
    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {ratingLabel(rating)}
    </span>
  );
}

// ─── Domain row ───────────────────────────────────────────────────────────────

function DomainRow({
  domain,
  ratingOptions,
  rating,
  rationale,
  onRatingChange,
  onRationaleChange,
}: {
  domain: Domain;
  ratingOptions: readonly string[];
  rating: string;
  rationale: string;
  onRatingChange: (v: string) => void;
  onRationaleChange: (v: string) => void;
}) {
  const [showGuidance, setShowGuidance] = useState(false);

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{domain.label}</span>
            <button
              onClick={() => setShowGuidance(!showGuidance)}
              className="text-muted-foreground hover:text-foreground"
              title="Show signalling questions"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </div>
          {showGuidance && (
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{domain.guidance}</p>
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap justify-end">
          {ratingOptions.map((opt) => (
            <button
              key={opt}
              onClick={() => onRatingChange(opt)}
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-all ${
                rating === opt
                  ? (RATING_COLOURS[opt] ?? "bg-primary text-primary-foreground border-primary") + " ring-2 ring-offset-1 ring-current"
                  : "bg-background hover:bg-secondary border-border text-muted-foreground"
              }`}
            >
              {ratingLabel(opt)}
            </button>
          ))}
        </div>
      </div>

      <textarea
        rows={2}
        value={rationale}
        onChange={(e) => onRationaleChange(e.target.value)}
        placeholder="Optional: explain your rating for this domain…"
        className="w-full px-2 py-1.5 border rounded bg-background text-xs font-sans focus:outline-none focus:ring-2 focus:ring-ring resize-y"
      />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function RiskOfBiasPage() {
  const costDisplay = useCostDisplay();
  const { studyId, reviewId } = useParams<{ studyId: string; reviewId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [ratings, setRatings] = useState<Record<string, string>>({});
  const [rationale, setRationale] = useState<Record<string, string>>({});
  const [overallRating, setOverallRating] = useState<string>("");
  const [initialised, setInitialised] = useState(false);
  const [saving, setSaving] = useState<"draft" | "confirm" | "unconfirm" | null>(null);
  const [savedThisVisit, setSavedThisVisit] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [aiUsageLine, setAiUsageLine] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["rob", studyId, reviewId],
    queryFn: () => api.get<RobData>(`/risk-of-bias/${studyId ?? ""}/${reviewId ?? ""}`),
    enabled: !!studyId && !!reviewId,
  });

  // Initialise form from fetched data — runs once when data first arrives
  useEffect(() => {
    if (!data || initialised) return;
    // Reopen on the tool the saved assessment actually used, not the default
    const savedTool = data.assessment?.tool && data.tools[data.assessment.tool] ? data.assessment.tool : null;
    const tool = selectedTool ?? savedTool ?? data.suggestedTool;
    setSelectedTool(tool);
    if (data.assessment) {
      setRatings(data.assessment.ratings ?? {});
      setRationale((data.assessment.rationale as Record<string, string>) ?? {});
      setOverallRating(data.assessment.overallRating ?? "");
    }
    setInitialised(true);
  }, [data, initialised, selectedTool]);

  const tool = selectedTool ? data?.tools[selectedTool] : null;
  const assessment = data?.assessment;

  const handleAiSuggest = async () => {
    setSuggesting(true);
    try {
      const result = await api.post<{
        ratings: Record<string, string>;
        rationale: Record<string, string>;
        overallRating: string | null;
        modelId?: string;
        usage?: { inputTokens: number; outputTokens: number };
      }>(`/risk-of-bias/${studyId ?? ""}/${reviewId ?? ""}/ai-suggest`, selectedTool ? { tool: selectedTool } : {});
      setRatings(result.ratings ?? {});
      setRationale(result.rationale ?? {});
      setOverallRating(result.overallRating ?? "");
      const usage = formatUsageWithCost(result.usage, costDisplay);
      setAiUsageLine(usage ? `${result.modelId ?? "AI"}: ${usage}` : null);
      if (!selectedTool && data?.suggestedTool) setSelectedTool(data.suggestedTool);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : "AI suggestion failed");
    } finally {
      setSuggesting(false);
    }
  };

  const currentSnapshot = JSON.stringify({ selectedTool, ratings, rationale, overallRating });
  const dirty = savedSnapshot !== null && currentSnapshot !== savedSnapshot;
  const { guard, dialog: leaveDialog } = useUnsavedChanges(dirty);
  // Baseline once the form has been filled from the server
  useEffect(() => {
    if (initialised && savedSnapshot === null) setSavedSnapshot(currentSnapshot);
  }, [initialised, savedSnapshot, currentSnapshot]);

  const handleUnconfirm = async () => {
    setSaving("unconfirm");
    setSaveErr(null);
    try {
      await api.post(`/risk-of-bias/${studyId ?? ""}/${reviewId ?? ""}/unconfirm`, {});
      await qc.invalidateQueries({ queryKey: ["rob", studyId, reviewId] });
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : "Could not mark as not confirmed.");
    } finally {
      setSaving(null);
    }
  };

  const handleSave = async (confirm: boolean) => {
    if (!selectedTool) return;
    setSaving(confirm ? "confirm" : "draft");
    setSaveErr(null);
    try {
      // Ratings from a previously selected tool would otherwise be saved under
      // this tool's name; keep only this tool's domains.
      const keys = new Set(tool?.domains.map((d) => d.key) ?? []);
      const pick = (m: Record<string, string>) => Object.fromEntries(Object.entries(m).filter(([k]) => keys.has(k)));
      await api.put(`/risk-of-bias/${studyId ?? ""}/${reviewId ?? ""}`, {
        tool: selectedTool,
        ratings: pick(ratings),
        rationale: pick(rationale),
        overallRating: overallRating || null,
        isConfirmed: confirm,
      });
      setSavedSnapshot(currentSnapshot);
      setSavedThisVisit(true);
      await qc.invalidateQueries({ queryKey: ["rob", studyId, reviewId] });
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(null);
    }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Back */}
      <button
        onClick={() => guard(() => navigate(-1))}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to extraction
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
        <div>
          <h1 className="text-xl font-bold">Quality Appraisal</h1>
          <p className="text-sm text-muted-foreground">Risk of bias assessment for this study</p>
        </div>
        {assessment?.isConfirmed && (
          <span className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-800 border border-green-200">
            Confirmed
          </span>
        )}
        {assessment?.isAiSuggested && !assessment.isConfirmed && (
          <span className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
            AI suggestion (unconfirmed)
          </span>
        )}
      </div>

      {/* Tool explainer */}
      <div className="border rounded-lg p-4 bg-secondary/20 space-y-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">What is risk of bias?</strong>{" "}
          Every systematic review must assess whether each study's design or conduct could have produced a
          systematically skewed result. This doesn't disqualify a study. It qualifies the certainty of the
          conclusions we can draw from it. Different assessment tools are used for different study types.
        </p>

        {/* One tool per review, set in Admin, Reviews. An assessment made
            before that with another tool still opens with its own tool. */}
        {tool && (
          <div>
            <p className="text-xs font-medium">Assessment tool for this review: {tool.label}</p>
            <p className="text-xs text-muted-foreground">{tool.description}</p>
            {data && selectedTool && selectedTool !== data.suggestedTool && (
              <p className="text-xs text-amber-700 mt-1.5">
                This paper was appraised with {tool.label} before the review's tool was set to{" "}
                {data.tools[data.suggestedTool]?.label ?? data.suggestedTool}. It is shown with the tool it was made with.
              </p>
            )}
          </div>
        )}
      </div>

      {/* AI suggestion */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleAiSuggest}
          disabled={suggesting}
          className="flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm hover:bg-accent disabled:opacity-50"
        >
          {suggesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          Suggest ratings with AI
        </button>
        <CostEstimate path={`/risk-of-bias/${studyId ?? ""}/${reviewId ?? ""}/ai-suggest/estimate`} compact />
        <p className="text-xs text-muted-foreground">
          AI reads the uploaded full-text PDF (or abstract) and suggests ratings. You must review and confirm each one.
        </p>
        {aiUsageLine && <p className="text-[11px] text-muted-foreground">Last AI suggestion: {aiUsageLine}</p>}
      </div>

      {/* Domain assessments */}
      {tool ? (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold">Domain ratings</h2>
          {tool.domains.map((domain) => (
            <DomainRow
              key={domain.key}
              domain={domain}
              ratingOptions={tool.ratings}
              rating={ratings[domain.key] ?? ""}
              rationale={rationale[domain.key] ?? ""}
              onRatingChange={(v) => setRatings((r) => ({ ...r, [domain.key]: v }))}
              onRationaleChange={(v) => setRationale((r) => ({ ...r, [domain.key]: v }))}
            />
          ))}

          {/* Overall rating */}
          <div className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">{tool.overallRule === "WORST_SCORE" ? "Overall rating" : "Overall Risk of Bias"}</span>
              <div className="flex gap-1.5 flex-wrap justify-end">
                {tool.overallRatings.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setOverallRating(opt)}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-all ${
                      overallRating === opt
                        ? (RATING_COLOURS[opt] ?? "bg-primary text-primary-foreground border-primary") + " ring-2 ring-offset-1 ring-current"
                        : "bg-background hover:bg-secondary border-border text-muted-foreground"
                    }`}
                  >
                    {ratingLabel(opt)}
                  </button>
                ))}
              </div>
            </div>
            {tool.overallRule === "WORST_SCORE" && (() => {
              const worst = worstScore(tool, ratings);
              return (
                <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                  {worst
                    ? <>The worst score counts: the lowest rating you have given is <RatingBadge rating={worst} />.</>
                    : "The worst score counts: rate the boxes this study evaluates and the lowest rating becomes the overall rating."}
                  {worst && worst !== overallRating && (
                    <button type="button" onClick={() => setOverallRating(worst)} className="text-primary hover:underline">
                      Use it as the overall rating
                    </button>
                  )}
                </p>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-lg p-4">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Select an assessment tool above to begin rating this study.
        </div>
      )}

      {/* Save actions */}
      {tool && (
        <div className="flex items-center gap-3 pt-2 border-t flex-wrap">
          {saveErr && <p className="text-xs text-destructive mr-auto">{saveErr}</p>}
          <div className="ml-auto">
            <SaveControls
              size="md"
              icon="shield"
              dirty={dirty}
              saving={saving}
              savedThisVisit={savedThisVisit}
              confirmed={assessment?.isConfirmed ?? false}
              confirmLabel="Confirm assessment"
              confirmedLabel="Assessment confirmed"
              unconfirmBody="The assessment stays saved as a draft and can be edited and confirmed again. This change is recorded in the audit log."
              onSave={() => void handleSave(false)}
              onConfirm={() => void handleSave(true)}
              onUnconfirm={() => void handleUnconfirm()}
            />
          </div>
        </div>
      )}
      {leaveDialog}

      {/* Summary table for confirmed assessment */}
      {assessment?.isConfirmed && tool && (
        <div className="border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-secondary/40 border-b">
            <span className="text-sm font-semibold">Confirmed Assessment Summary</span>
            {assessment.assessor && (
              <span className="text-xs text-muted-foreground ml-2">assessed by {assessment.assessor.name}</span>
            )}
          </div>
          <table className="w-full text-xs">
            <tbody className="divide-y">
              {tool.domains.map((d) => (
                <tr key={d.key} className="hover:bg-secondary/20">
                  <td className="px-4 py-2.5 font-medium">{d.label}</td>
                  <td className="px-4 py-2.5"><RatingBadge rating={assessment.ratings[d.key]} /></td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {(assessment.rationale as Record<string, string>)?.[d.key] ?? ""}
                  </td>
                </tr>
              ))}
              <tr className="bg-secondary/30 font-semibold">
                <td className="px-4 py-2.5">Overall</td>
                <td className="px-4 py-2.5"><RatingBadge rating={assessment.overallRating ?? undefined} /></td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
